import { motion } from 'framer-motion'
import { Fragment } from 'react'

import useRotate from './useRotate'
import type { WheelItem, WheelItemsData } from './wheelData'
import WheelLabel from './WheelLabel'
import type { TextRotation } from './WheelLabel'
import WheelSection from './WheelSection'

type Props = {
  activeItems: Set<WheelItem>
  itemOnSelect: (item: WheelItem) => void
  wheelItemsData: WheelItemsData
  searchText: string
}

export default function Wheel({ activeItems, itemOnSelect, wheelItemsData, searchText }: Props) {
  const { rotation, onMouseDown, groupRef } = useRotate()
  const isSearching = searchText.trim().length > 0

  return (
    <div className="wheel-container h-[700px]">
      <Wiggle>
        <svg className="h-[700px] w-[700px]">
          <g
            ref={groupRef}
            className="translate-x-[350px] translate-y-[350px] rotate-(--wheel-rotation) cursor-pointer"
            style={{ '--wheel-rotation': `${rotation}deg` } as React.CSSProperties}
            onMouseDown={onMouseDown}
          >
            {Object.entries(wheelItemsData).map(([key, { path, label: labelItem }]) => {
              const isFound = labelItem.label.toLowerCase().startsWith(searchText.toLowerCase())
              return (
                <Fragment key={key}>
                  <WheelSection
                    key={`path-${key}`}
                    item={key as WheelItem}
                    d={path.d}
                    color={path.color}
                    isActive={activeItems.has(key as WheelItem)}
                    itemOnSelect={itemOnSelect}
                    isSearching={isSearching}
                    isFound={isFound}
                  />
                </Fragment>
              )
            })}
            {Object.entries(wheelItemsData).map(([key, { label: labelItem }]) => {
              const textRotation = getTextRotation(labelItem.angle, rotation)
              return (
                <WheelLabel
                  key={`label-${key}`}
                  angle={labelItem.angle}
                  translate={labelItem.translate}
                  label={labelItem.label}
                  textRotation={textRotation}
                />
              )
            })}
          </g>
        </svg>
      </Wiggle>
    </div>
  )
}

function getTextRotation(baseAngle: number, wheelRotation: number): TextRotation {
  const absAngle = (baseAngle + wheelRotation) % 360
  const normAngle = absAngle < 0 ? absAngle + 360 : absAngle
  return normAngle > 90 && normAngle < 270 ? 180 : 0
}

function Wiggle({ children }: { children?: React.ReactNode }) {
  return (
    <motion.div
      animate={{
        rotate: [0, 15, -15, 0],
      }}
      transition={{ duration: 1.5, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  )
}
